import { Lock, Star, Award, Trophy, Handshake, CreditCard, Archive, BookOpen, ShoppingBag, TrendingUp, Layers, PenTool, ShieldCheck, Package, ShieldOff } from 'lucide-react';
import { TrophyWithProgress } from '@/types';

const TIER_COLORS = [
  '',
  'from-amber-400   to-amber-600',    // Tier 1 — Bronze
  'from-slate-300   to-slate-500',    // Tier 2 — Silver
  'from-yellow-400  to-yellow-600',   // Tier 3 — Gold
  'from-cyan-400    to-cyan-600',     // Tier 4 — Platinum
  'from-violet-500  to-violet-700',   // Tier 5 — Diamond
];

const ICON_MAP: Record<string, React.ElementType> = {
  Handshake, CreditCard, Archive, BookOpen, Star, Award, Trophy,
  ShoppingBag, TrendingUp, Layers, PenTool, ShieldCheck,
};

function TrophyIcon({ name, className }: { name: string; className?: string }) {
  const Icon = ICON_MAP[name] ?? Package;
  return <Icon className={className} />;
}

interface Props {
  trophy: TrophyWithProgress;
  onClick?: () => void;
  size?: 'sm' | 'md';
  ageRestricted?: boolean;
}

export default function TrophyCard({ trophy, onClick, size = 'md', ageRestricted = false }: Props) {
  const locked     = !trophy.earned;
  const tierGrad   = TIER_COLORS[trophy.tier] ?? TIER_COLORS[1];
  const progress   = Math.min(100, trophy.requirement_value > 0 ? Math.round((trophy.progress / trophy.requirement_value) * 100) : 0);

  if (size === 'sm') {
    return (
      <button
        onClick={onClick}
        title={ageRestricted ? `${trophy.name} — Available when you turn 18` : trophy.name}
        className={`relative flex flex-col items-center justify-center aspect-square rounded-xl border-2 transition-all hover:scale-105 ${
          ageRestricted
            ? 'border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-900/10 opacity-60'
            : locked
              ? 'border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 opacity-50'
              : 'border-transparent shadow-sm hover:shadow-md'
        }`}
      >
        {!locked && !ageRestricted && (
          <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${tierGrad} opacity-10`} />
        )}
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          ageRestricted
            ? 'bg-amber-100 dark:bg-amber-900/40'
            : locked ? 'bg-gray-200 dark:bg-zinc-700' : `bg-gradient-to-br ${tierGrad}`
        }`}>
          {ageRestricted
            ? <ShieldOff className="h-4 w-4 text-amber-500 dark:text-amber-400" />
            : locked
              ? <Lock className="h-4 w-4 text-gray-500 dark:text-zinc-500" />
              : <TrophyIcon name={trophy.icon} className="h-4 w-4 text-white" />
          }
        </div>
        <p className="mt-1 text-xs font-medium text-gray-700 dark:text-zinc-300 text-center leading-tight px-1 line-clamp-2">{trophy.name}</p>
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`relative w-full text-left rounded-2xl border-2 p-4 transition-all hover:scale-[1.01] hover:shadow-md ${
        ageRestricted
          ? 'border-amber-200 dark:border-amber-900/50 bg-amber-50/30 dark:bg-amber-900/10'
          : locked
            ? 'border-gray-200 dark:border-zinc-800 bg-white dark:bg-zinc-900'
            : 'border-transparent bg-white dark:bg-zinc-900 shadow-sm'
      }`}
    >
      {!locked && !ageRestricted && (
        <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${tierGrad} opacity-5`} />
      )}

      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`w-12 h-12 rounded-xl flex-shrink-0 flex items-center justify-center ${
          ageRestricted
            ? 'bg-amber-100 dark:bg-amber-900/40'
            : locked ? 'bg-gray-200 dark:bg-zinc-700' : `bg-gradient-to-br ${tierGrad}`
        }`}>
          {ageRestricted
            ? <ShieldOff className="h-5 w-5 text-amber-500 dark:text-amber-400" />
            : locked
              ? <Lock className="h-5 w-5 text-gray-500 dark:text-zinc-500" />
              : <TrophyIcon name={trophy.icon} className="h-5 w-5 text-white" />
          }
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className={`font-semibold text-sm ${ageRestricted || locked ? 'text-gray-500 dark:text-zinc-500' : 'text-gray-900 dark:text-zinc-100'}`}>
              {trophy.name}
            </p>
            <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${
              ageRestricted
                ? 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40'
                : locked
                  ? 'text-gray-500 dark:text-zinc-500 bg-gray-100 dark:bg-zinc-800'
                  : `text-white bg-gradient-to-r ${tierGrad}`
            }`}>
              T{trophy.tier}
            </span>
          </div>
          <p className={`text-xs mt-0.5 leading-relaxed ${ageRestricted || locked ? 'text-gray-500 dark:text-zinc-500' : 'text-gray-600 dark:text-zinc-400'}`}>
            {trophy.description}
          </p>

          {/* Age restriction notice */}
          {ageRestricted && (
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 font-medium">
              Available when you turn 18
            </p>
          )}

          {/* Progress bar for locked trophies that have progress (not age-restricted) */}
          {!ageRestricted && locked && trophy.progress > 0 && (
            <div className="mt-2">
              <div className="flex justify-between text-xs text-gray-500 dark:text-zinc-500 mb-1">
                <span>{trophy.progress.toLocaleString()} / {trophy.requirement_value.toLocaleString()}</span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 bg-gray-200 dark:bg-zinc-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-400 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Bonus XP pill */}
          {!ageRestricted && trophy.bonus_xp > 0 && (
            <div className="mt-2">
              <span className="text-xs text-brand-600 dark:text-brand-400 font-medium">+{trophy.bonus_xp} XP bonus</span>
            </div>
          )}

          {/* Earned date */}
          {trophy.earned && trophy.earned_at && (
            <p className="text-xs text-gray-500 dark:text-zinc-500 mt-1">
              Earned {new Date(trophy.earned_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}
